import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button } from "@mui/joy";
import { Box, Divider, Paper, Tooltip } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "react-redux";
import {
  currencyFormatter,
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { Expense_Entity } from "../../entities/expense";
import { Project } from "../../entities/project";
import { greyStyleForNonEditableCells } from "../../helpers/dataGridHelpers";
import { isAdminSelector } from "../../redux/appStatus/appStatusSelector";
import {
  useCreateExpenseMutation,
  useDeleteExpenseMutation,
  useUpdateExpenseMutation,
} from "../../redux/expense/expenseEndpoints";
import { useExpenses } from "../../redux/expense/expenseHooks";
import { selectedProjectID } from "../../redux/filmsReducer";
import { useProject } from "../../redux/project/projectHooks";
import { useAppSelector } from "../hooks";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { usePendingEdits } from "../pendingEdits/pendingEdits";
import { useSaveError } from "../saveError/saveError";
import { useEffect, useMemo } from "react";

/**
 * Expense
 *
 * @returns {null} Expense
 */
export default function Expenses() {
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const { expenses } = useExpenses({
    filters: JSON.stringify([
      {
        name: "expenses.projectID",
        value: projectID,
        comparison: "eq",
      },
    ]),
  });
  const [updateExpense] = useUpdateExpenseMutation();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();
  const [deleteExpense] = useDeleteExpenseMutation();
  const { reportSaveError, saveErrorSnackbar } = useSaveError();
  const {
    pendingEdits,
    pendingValue,
    markPending,
    dropPending,
    releaseSettled,
  } = usePendingEdits();

  // Fresh server data: let go of the cells it has caught up with.
  useEffect(() => {
    releaseSettled((id, field) => {
      const expense = expenses?.find((e) => e.id === Number(id));
      return expense?.[field];
    });
  }, [expenses, releaseSettled]);

  // Overlay the values committed but not yet echoed back, so a later refetch
  // can't revert them.
  const rowsWithPendingEdits = useMemo(
    () =>
      (expenses ?? []).map((expense) => {
        const held = Object.keys(pendingEdits).filter((key) =>
          key.startsWith(`${expense.id}:`)
        );
        if (!held.length) return expense;
        const row = { ...expense } as Expense_Entity;
        held.forEach((key) => {
          const field = key.slice(key.indexOf(":") + 1);
          row[field] = pendingValue(expense.id, field, row[field]);
        });
        return row;
      }),
    [expenses, pendingEdits, pendingValue]
  );

  const isAdmin = useAppSelector(isAdminSelector);

  // useEffect(() => {
  //   if (expenses.length) {
  //     if (expenses.length > count) scrollToBottom();
  //     setCount(expenses.length);
  //   }
  // }, [expenses]);

  // function scrollToBottom() {
  //   if (expenses?.length && apiRef && apiRef.current) {
  //     try {
  //       apiRef.current.scrollToIndexes({
  //         rowIndex: expenses.length - 1,
  //         colIndex: 0,
  //       });
  //     } catch (error) {
  //       console.log("Unable to scroll");
  //     }
  //   }
  // }

  const onExpenseUpdate = async (
    _newExpense: Expense_Entity,
    _oldExpense: Expense_Entity
  ) => {
    const editedFields = Object.keys(_newExpense).filter(
      (key) => _newExpense[key] !== _oldExpense[key]
    );

    // Hold the edited fields until the server reports them, so a later refetch
    // can't revert them. Not awaited: holding the cell in edit mode for a
    // round-trip swallows whatever is typed next.
    editedFields.forEach((field) =>
      markPending(_newExpense.id, field, _newExpense[field])
    );

    updateExpense({ id: _newExpense.id, body: _newExpense })
      .unwrap()
      .catch((error) => {
        editedFields.forEach((field) => dropPending(_newExpense.id, field));
        reportSaveError(error);
      });

    return { ..._newExpense };
  };

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        isCellEditable={(params) =>
          project?.locked() ? false : params.colDef.editable ?? false
        }
        getCellClassName={(p) => {
          if (project?.locked()) return "lock";
          const classes = greyStyleForNonEditableCells(p);
          return classes;
        }}
        apiRef={apiRef}
        processRowUpdate={onExpenseUpdate}
        onProcessRowUpdateError={reportSaveError}
        rows={rowsWithPendingEdits}
        columns={expenseColumns(project!, confirm, deleteExpense)}
        getDetailPanelHeight={() => 100}
        showToolbar
        slots={{
          toolbar: ExpenseToolbar,
          noRowsOverlay: NoRowsOverlay,
          footer: ExpensesFooter,
        }}
        slotProps={{
          footer: { rows: expenses },
          cell: { tabIndex: 1 },
        }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              amount: isAdmin,
              unitPrice: isAdmin,
            },
          },
        }}
      />
      {saveErrorSnackbar}
    </Paper>
  );
}

function ExpenseToolbar() {
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const [createExpense] = useCreateExpenseMutation();

  const handleNewExpense = () => {
    if (projectID && !project?.locked()) {
      const newExpense = {
        projectID: projectID,
        description: "",
        quantity: 100,
        unitPrice: 0,
        amount: 0,
        createdAt: "",
        updatedAt: "",
        id: -1,
      };
      createExpense(newExpense);
    }
  };

  useHotkeys(`ctrl+enter`, handleNewExpense, {
    enableOnTags: ["INPUT", "SELECT", "TEXTAREA"],
  });

  return (
    <Toolbar>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
      >
        <Tooltip title="ctrl + enter">
          <Button
            size="sm"
            variant="soft"
            onClick={handleNewExpense}
            disabled={project?.locked()}
          >
            + New Expense
          </Button>
        </Tooltip>
        <Divider
          sx={{ margin: 1, width: 2 }}
          orientation="vertical"
          variant="middle"
          flexItem
        />
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
      </Box>
    </Toolbar>
  );
}

function ExpensesFooter({ rows }: { rows: Expense_Entity[] }) {
  let total = 0;

  for (const key in rows) {
    if (Object.prototype.hasOwnProperty.call(rows, key)) {
      const row = rows[key];
      total += row.amount;
    }
  }
  return (
    <>
      <Divider />
      <Box sx={{ p: 2, textAlign: "right", fontWeight: 600 }}>
        Supplies: {currencyFormatter.format(total / 10000)}
      </Box>
    </>
  );
}

const expenseColumns = (
  project: Project,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  deleteExpense: (id: number) => void
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      type: "string",
      width: 40,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      editable: true,
      type: "string",
    },
    {
      field: "quantity",
      headerName: "Quantity",
      editable: true,
      type: "number",
      valueGetter: getterDivider("quantity", 100),
      valueSetter: setterDivider("quantity", 100),
    },
    {
      field: "unitPrice",
      headerName: "Unit Price",
      editable: true,
      type: "number",
      valueGetter: getterDivider("unitPrice", 10000),
      valueSetter: setterDivider("unitPrice", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${currencyFormatter.format(valueOrParams?.value ?? valueOrParams)}`,
    },
    {
      field: "amount",
      headerName: "Amount",
      editable: false,
      type: "number",
      valueGetter: getterDivider("amount", 10000),
      valueSetter: setterDivider("amount", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${currencyFormatter.format(valueOrParams?.value ?? valueOrParams)}`,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (props: { id: any }) => [
        <GridActionsCellItem
          key="delete"
          disabled={project.locked()}
          icon={<DeleteIcon />}
          label={"Delete"}
          onClick={() => {
            confirm({
              description: `You are about to delete an expense`,
            })
              .then(() => {
                deleteExpense(props.id);
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);
